const TvgHeader = {
  init() {
    const header = document.querySelector<HTMLElement>('[data-tvg-header]')

    if (!header) {
      return
    }

    const dropdownTriggers = Array.from(header.querySelectorAll<HTMLButtonElement>('[data-tvg-dropdown-trigger]'))
    const mobileTrigger = header.querySelector<HTMLButtonElement>('[data-tvg-mobile-trigger]')
    const mobileNavigation = header.querySelector<HTMLElement>('[data-tvg-mobile-navigation]')
    const mobileTriggerLabel = mobileTrigger?.querySelector<HTMLElement>('.sr-only')
    const mobileBack = mobileNavigation?.querySelector<HTMLButtonElement>('[data-tvg-mobile-back]')
    const mobileBackText = mobileNavigation?.querySelector<HTMLElement>('[data-tvg-mobile-back-text]')
    const mobileLevels = Array.from(mobileNavigation?.querySelectorAll<HTMLElement>('[data-tvg-mobile-level]') ?? [])
    const mobileLevelTriggers = Array.from(
      mobileNavigation?.querySelectorAll<HTMLButtonElement>('[data-tvg-mobile-level-trigger]') ?? []
    )
    const initialLevelId = mobileNavigation?.dataset.tvgMobileInitialLevel ?? 'root'
    const levelStack: string[] = []
    let hideLevelsTimeout: number | null = null

    const getLevel = (id: string) => mobileLevels.find((level) => level.dataset.tvgMobileLevel === id)

    const getLevelTrigger = (id: string) =>
      mobileLevelTriggers.find((trigger) => trigger.dataset.tvgMobileLevelTrigger === id)

    const getLevelStack = (id: string) => {
      const stack: string[] = []
      let level = getLevel(id)

      while (level && level.dataset.tvgMobileLevel !== 'root') {
        stack.unshift(level.dataset.tvgMobileLevel ?? '')
        level = getLevel(level.dataset.tvgMobileLevelParent ?? 'root')
      }

      return ['root', ...stack]
    }

    const updateMobileBar = () => {
      const currentLevelId = levelStack[levelStack.length - 1]
      const isNested = currentLevelId !== 'root'

      mobileNavigation?.classList.toggle('tvg-header__mobile-nav--nested', isNested)

      if (mobileBack) {
        mobileBack.disabled = !isNested
        mobileBack.setAttribute('aria-hidden', String(!isNested))
      }

      if (mobileBackText && isNested) {
        mobileBackText.textContent = getLevelTrigger(currentLevelId)?.dataset.tvgMobileBackLabel ?? ''
      }
    }

    const clearHideLevelsTimeout = () => {
      if (hideLevelsTimeout === null) {
        return
      }

      window.clearTimeout(hideLevelsTimeout)
      hideLevelsTimeout = null
    }

    const showLevel = (id: string, direction: 'forward' | 'back') => {
      const currentLevel = mobileLevels.find((level) => level.classList.contains('tvg-header__mobile-level--active'))
      const nextLevel = getLevel(id)

      if (!nextLevel || nextLevel === currentLevel) {
        return
      }

      clearHideLevelsTimeout()
      nextLevel.hidden = false
      nextLevel.classList.toggle('tvg-header__mobile-level--previous', direction === 'back')
      // The level is coming out of `hidden`; without a layout flush the class change below lands in
      // the same style change and the level snaps into place instead of sliding.
      nextLevel.getBoundingClientRect()

      currentLevel?.classList.remove('tvg-header__mobile-level--active')
      currentLevel?.classList.toggle('tvg-header__mobile-level--previous', direction === 'forward')
      nextLevel.classList.remove('tvg-header__mobile-level--previous')
      nextLevel.classList.add('tvg-header__mobile-level--active')

      hideLevelsTimeout = window.setTimeout(() => {
        mobileLevels
          .filter((level) => level !== nextLevel)
          .forEach((level) => {
            level.hidden = true
            level.classList.remove('tvg-header__mobile-level--previous')
          })

        hideLevelsTimeout = null
      }, 543)

      updateMobileBar()
    }

    const resetLevels = () => {
      clearHideLevelsTimeout()
      levelStack.splice(0, levelStack.length, ...getLevelStack(initialLevelId))

      mobileLevels.forEach((level) => {
        const isActive = level.dataset.tvgMobileLevel === levelStack[levelStack.length - 1]

        level.hidden = !isActive
        level.classList.toggle('tvg-header__mobile-level--active', isActive)
        level.classList.remove('tvg-header__mobile-level--previous')
      })

      updateMobileBar()
    }

    const getDropdown = (trigger: HTMLButtonElement) =>
      document.getElementById(trigger.getAttribute('aria-controls') ?? '')

    const setDropdown = (trigger: HTMLButtonElement, isOpen: boolean) => {
      const dropdown = getDropdown(trigger)

      if (!dropdown) {
        return
      }

      trigger.setAttribute('aria-expanded', String(isOpen))
      dropdown.hidden = !isOpen
    }

    const closeDropdown = () => {
      dropdownTriggers.forEach((trigger) => setDropdown(trigger, false))
    }

    const setMobileNavigation = (isOpen: boolean) => {
      if (!mobileTrigger || !mobileNavigation) {
        return
      }

      if (isOpen) {
        resetLevels()
      }

      mobileTrigger.setAttribute('aria-expanded', String(isOpen))
      mobileNavigation.hidden = !isOpen
      document.body.classList.toggle('tvg-mobile-nav-open', isOpen)

      if (mobileTriggerLabel) {
        mobileTriggerLabel.textContent = isOpen ? 'Close menu' : 'Open menu'
      }
    }

    const closeMobileNavigation = () => {
      setMobileNavigation(false)
    }

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true'

        // Only one dropdown is open at a time.
        closeDropdown()
        setDropdown(trigger, !isOpen)
      })
    })

    mobileTrigger?.addEventListener('click', () => {
      if (!mobileNavigation) {
        return
      }

      const isOpen = mobileTrigger.getAttribute('aria-expanded') === 'true'
      setMobileNavigation(!isOpen)
    })

    mobileNavigation?.querySelectorAll<HTMLAnchorElement>('a').forEach((link) => {
      link.addEventListener('click', closeMobileNavigation)
    })

    mobileLevelTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const levelId = trigger.dataset.tvgMobileLevelTrigger

        if (levelId && getLevel(levelId)) {
          levelStack.push(levelId)
          showLevel(levelId, 'forward')
        }
      })
    })

    mobileBack?.addEventListener('click', () => {
      if (levelStack.length > 1) {
        levelStack.pop()
        showLevel(levelStack[levelStack.length - 1], 'back')
      }
    })

    document.addEventListener('click', (event) => {
      if (!header.contains(event.target as Node)) {
        closeDropdown()
      }
    })

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDropdown()
        closeMobileNavigation()
      }
    })

    setMobileNavigation(false)
  },
}

export default TvgHeader
